import { Subject, takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@auth/services/auth/auth.service';
import { InventoryItemsService } from '@inventory/services/inventory-items/inventory-items.service';
import { IJobCardForm, IJobCardMaterialForm, JobCardViewSteps } from '@job-card/models/job-card';
import { JobCardStorageService } from '@job-card/services/job-card-storage/job-card-storage.service';
import {
	leadAssessmentRestrictedStatusOptions,
	leadAssessmentStatusOptions,
} from '@leads/client-facing/models/domain/scheduled-assessment.domain';
import { AssessmentStatusDropdownComponent } from '@leads/shared/components/assessment-status-dropdown/assessment-status-dropdown.component';
import { ScheduledAssessment, ScheduledAssessmentStatus } from '@leads/shared/models/domain/scheduled-assessment.domain';
import { LeadAssessmentService } from '@leads/shared/services/lead-assessment/lead-assessment.service';
import { CalendarEventColours } from '@schedule/lib/classes/event-colours';
import { TimeRangeValidator } from '@shared/classes/time-validator';
import { AlertComponent } from '@shared/components/alerts/alert/alert.component';
import { StandardControlComponent } from '@shared/components/forms/standard-control/standard-control.component';
import { TextareaControlComponent } from '@shared/components/forms/textarea-control/textarea-control.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { HeaderPopoverComponent } from '@shared/components/sections/header-popover/header-popover.component';
import { StepNavigatorComponent } from '@shared/components/ui/step-navigator/step-navigator.component';
import { AlertTypes } from '@shared/models/alert';
import { IPopover } from '@shared/models/popover';
import { IStepNavigation, IStepNavigatorConfig, NavButtonStyle, StepDisplay, StepModes, StepUI } from '@shared/models/step-navigator';
import { ToastTypes } from '@shared/models/toast';
import { OffcanvasService } from '@shared/services/offcanvas/offcanvas.service';
import { ToastService } from '@shared/services/toast/toast.service';

import { JobCardAttachmentsComponent } from '../job-card-attachments/job-card-attachments.component';
import { JobCardMaterialsComponent } from '../job-card-materials/job-card-materials.component';
import { JobCardViewAndSignOffcanvasComponent } from '../job-card-view-and-sign-offcanvas/job-card-view-and-sign-offcanvas.component';

@Component({
	selector: 'app-job-card-offcanvas',
	imports: [
		CommonModule,
		StandardControlComponent,
		LoadingStateComponent,
		AssessmentStatusDropdownComponent,
		TextareaControlComponent,
		StepNavigatorComponent,
		JobCardMaterialsComponent,
		JobCardAttachmentsComponent,
		AlertComponent,
		HeaderPopoverComponent,
	],
	templateUrl: './job-card-offcanvas.component.html',
	styleUrl: './job-card-offcanvas.component.scss',
	host: {
		class: 'h-100',
		style: 'display: contents',
	},
})
export class JobCardOffcanvasComponent implements OnInit, OnDestroy {
	@Input() leadAssessmentId: string;
	@Input() isOwner: boolean;

	private offcanvasService = inject(OffcanvasService);
	private leadAssessmentService = inject(LeadAssessmentService);
	private inventoryItemsService = inject(InventoryItemsService);
	private jobCardStorageService = inject(JobCardStorageService);
	private authService = inject(AuthService);
	private toastService = inject(ToastService);

	destroyed$ = new EventEmitter<void>();
	readonly leadAssessmentStatusOptions = leadAssessmentStatusOptions;
	readonly leadAssessmentRestrictedStatusOptions = leadAssessmentRestrictedStatusOptions;
	readonly AlertTypes = AlertTypes;

	scheduledAssessment: ScheduledAssessment;
	form: FormGroup<IJobCardForm>;
	isLoading = true;
	isSaving = false;
	mustRefreshAttachments: Subject<void> = new Subject<void>();

	navigationConfig: IStepNavigatorConfig;
	selectedStep: IStepNavigation;
	JobCardViewSteps = JobCardViewSteps;

	inventoryItemsList: Array<string> = [];

	ngOnInit(): void {
		this.navigationConfig = {
			ui: StepUI.WIZARD,
			mode: StepModes.NG_SWITCH,
			displayType: StepDisplay.TABS,
			navButtonStyle: NavButtonStyle.CHEVRON,
			showStepChevrons: false,
			showStepNumbers: false,
			showNavButtons: true,
			steps: [
				{
					title: {
						full: 'Materials',
						tabs: 'Materials',
					},
					icon: 'bi-clipboard',
					switchCase: JobCardViewSteps.MATERIALS,
				},
				{
					title: {
						full: 'Attachments',
						tabs: 'Attachments',
					},
					icon: 'bi-telephone',
					switchCase: JobCardViewSteps.ATTACHMENTS,
				},
			],
		};

		this.selectedStep = this.navigationConfig.steps[0];

		this.leadAssessmentService
			.getScheduledAssessment(this.leadAssessmentId)
			.pipe(takeUntil(this.destroyed$))
			.subscribe((scheduledAssessment) => {
				if (scheduledAssessment) {
					this.scheduledAssessment = scheduledAssessment;
					this.buildForm();
					this.inventoryItemsService
						.getInventoryItemNamesByOrganisationId(this.scheduledAssessment.organisationId)
						.pipe(takeUntil(this.destroyed$))
						.subscribe((items) => {
							this.inventoryItemsList = items;
						});
				}
				this.isLoading = false;
			});
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
		this.mustRefreshAttachments.complete();
	}

	get materialsFormItems(): FormArray<FormGroup<IJobCardMaterialForm>> {
		return this.form.controls.materials;
	}

	buildForm(): void {
		let materialsFormItems: Array<FormGroup<IJobCardMaterialForm>> = [];
		if (this.scheduledAssessment.jobCard.materials) {
			materialsFormItems = this.scheduledAssessment.jobCard.materials.map((material) => {
				return new FormGroup<IJobCardMaterialForm>({
					quantity: new FormControl<number | null>(material.quantity, [Validators.required, Validators.min(1)]),
					description: new FormControl<string | null>(material.description, [
						Validators.required,
						Validators.minLength(1),
						Validators.maxLength(100),
					]),
					amount: new FormControl<number | null>(material.amount.amount, [Validators.min(0)]),
				});
			});
		}
		this.form = new FormGroup<IJobCardForm>(
			{
				startTime: new FormControl<string | null>(this.convertTimestampToTimeString(this.scheduledAssessment.jobCard.startTime)),
				endTime: new FormControl<string | null>(this.convertTimestampToTimeString(this.scheduledAssessment.jobCard.endTime)),
				jobNotes: new FormControl<string | null>(this.scheduledAssessment.jobCard.notes),
				materials: new FormArray<FormGroup<IJobCardMaterialForm>>(materialsFormItems),
				attachments: new FormControl<File[] | null>([]),
			},
			[TimeRangeValidator.validate<IJobCardForm>('startTime', 'endTime')],
		);
	}

	viewAndSign(): void {
		if (this.form.dirty) {
			this.saveJobCard();
		}
		const ref = this.offcanvasService.open(JobCardViewAndSignOffcanvasComponent, 'end', true, 'oc-lg');
		const componentInstance = ref.componentInstance as JobCardViewAndSignOffcanvasComponent;
		componentInstance.scheduledAssessment = this.scheduledAssessment;
		componentInstance.scheduledAssessmentId = this.leadAssessmentId;
		componentInstance.mustRefreshAttachments = this.mustRefreshAttachments;
	}

	addMaterialFormItem(): void {
		this.materialsFormItems.push(
			new FormGroup<IJobCardMaterialForm>({
				quantity: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
				description: new FormControl<string | null>(null, [
					Validators.required,
					Validators.minLength(1),
					Validators.maxLength(100),
				]),
				amount: new FormControl<number | null>(null, [Validators.min(0)]),
			}),
		);
		this.materialsFormItems.markAsDirty();
	}

	removeMaterialFormItem(index: number): void {
		this.materialsFormItems.removeAt(index);
		this.materialsFormItems.markAsDirty();
	}

	convertTimestampToTimeString(timestamp: Timestamp | null | undefined): string | null {
		if (!timestamp) {
			return null;
		}

		const date = timestamp.toDate();
		const hours = date.getHours().toString().padStart(2, '0');
		const minutes = date.getMinutes().toString().padStart(2, '0');

		return `${hours}:${minutes}`;
	}

	convertTimeToTimestamp(timeString: string): Timestamp | null {
		if (!timeString) {
			return null;
		}
		const [hours, minutes] = timeString.split(':').map(Number);
		const now = new Date();
		now.setHours(hours, minutes, 0, 0);

		return Timestamp.fromDate(now);
	}

	close(): void {
		this.offcanvasService.dismissLast();
	}

	startJob(): void {
		if (this.form.dirty) {
			this.toastService.showToast({
				title: 'Job Card',
				message: 'Please save your changes before starting the job.',
				type: ToastTypes.WARNING,
			});
			return;
		}
		this.leadAssessmentService.updateScheduledAssessmentJobStartTime(this.leadAssessmentId, Timestamp.now());
	}

	endJob(): void {
		if (this.form.dirty) {
			this.toastService.showToast({
				title: 'Job Card',
				message: 'Please save your changes before ending the job.',
				type: ToastTypes.WARNING,
			});
			return;
		}
		this.leadAssessmentService.updateScheduledAssessmentJobEndTime(this.leadAssessmentId, Timestamp.now());
	}

	statusChange(status: ScheduledAssessmentStatus): void {
		this.leadAssessmentService.updateScheduledAssessmentStatus(this.leadAssessmentId, status);
	}

	saveJobCard(): void {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		this.isSaving = true;
		const updateJobCardPromise = this.leadAssessmentService.updateScheduledAssessmentJobCard(
			this.leadAssessmentId,
			this.form.controls.jobNotes.value!,
			this.form.controls.materials.value.map((material) => ({
				quantity: material.quantity!,
				description: material.description!,
				amount: {
					amount: material.amount!,
					code: 'ZAR',
					symbol: 'R',
				},
			})),
			this.convertTimeToTimestamp(this.form.controls.startTime.value!),
			this.convertTimeToTimestamp(this.form.controls.endTime.value!),
		);

		const uploadAttachmentsPromise = this.form.controls.attachments.value
			? Promise.all(
					this.form.controls.attachments.value.map((file) =>
						this.jobCardStorageService.uploadAssessmentAttachment(
							this.scheduledAssessment.organisationId,
							this.scheduledAssessment.leadRequestId,
							this.leadAssessmentId,
							file,
							this.authService.currentUser?.displayName || 'unknown',
						),
					),
				)
			: Promise.resolve();

		Promise.all([updateJobCardPromise, uploadAttachmentsPromise])
			.then(() => {
				this.form.controls.attachments.setValue([]);
				this.form.markAsPristine();
				this.mustRefreshAttachments.next();
				this.showSuccessToast();
			})
			.finally(() => {
				this.isSaving = false;
			});
	}

	getScheduledAssessmentColour(status: ScheduledAssessmentStatus): string {
		return CalendarEventColours.scheduledAssessment(status).secondary;
	}

	showSuccessToast(): void {
		this.toastService.showToast({
			title: 'Job Card',
			message: 'Your changes have been saved.',
			type: ToastTypes.SUCCESS,
		});
	}

	jobNotesPopOver: IPopover = {
		triggerText: {
			desktop: "What's this?",
			mobile: "What's this?",
		},
		title: 'Job Notes',
		description: 'Add quick updates while working. Shown to the client on the job card',
		icon: 'bi-info-circle-fill',
	};
}
